import React, { useEffect, useState } from "react";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

const money = (value) =>
  `PHP ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const currentMonth = () =>
  new Date().toLocaleString("en-US", { month: "long", year: "numeric" });

export default function AdminPaymentStatements() {
  const [month, setMonth] = useState(currentMonth());
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [data, setData] = useState({
    month_paid_for: "",
    summary: {},
    rows: [],
  });

  const load = async (selectedMonth = month) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/payments/statements", {
        params: { month_paid_for: selectedMonth },
      });
      setData(res.data || { month_paid_for: "", summary: {}, rows: [] });
    } catch (error) {
      console.error(error);
      setData({ month_paid_for: "", summary: {}, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(currentMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true" src={bgVideo} autoPlay muted loop playsInline className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">Admin Payments</p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">Monthly Statements</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Property-linked monthly statement view for admin monitoring and reconciliation.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900"
              placeholder="April 2026"
            />
            <button
              onClick={() => load(month)}
              className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
            >
              Load
            </button>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading statements...
          </div>
        ) : data.rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/15 bg-slate-950/70 py-10 text-center text-slate-300">
            No statement rows found.
          </div>
        ) : (
          <div className="grid gap-3">
            {data.rows.map((row) => (
              <article key={`${row.property_id}-${row.tenant_id}`} className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-lg font-bold text-white">{row.property_name}</p>
                    <p className="text-sm text-slate-400">{row.tenant_name || "No tenant"} - {row.location || "-"}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                    <div className="rounded border border-white/10 bg-white/5 p-2">
                      <p className="text-slate-500">Expected</p>
                      <p className="mt-1 font-semibold text-white">{money(row.expected_amount)}</p>
                    </div>
                    <div className="rounded border border-white/10 bg-white/5 p-2">
                      <p className="text-slate-500">Approved</p>
                      <p className="mt-1 font-semibold text-teal-200">{money(row.approved_amount)}</p>
                    </div>
                    <div className="rounded border border-white/10 bg-white/5 p-2">
                      <p className="text-slate-500">Pending</p>
                      <p className="mt-1 font-semibold text-amber-200">{money(row.pending_amount)}</p>
                    </div>
                    <div className="rounded border border-white/10 bg-white/5 p-2">
                      <p className="text-slate-500">Balance</p>
                      <p className="mt-1 font-semibold text-amber-200">{money(row.remaining_balance)}</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpanded((prev) => ({ ...prev, [row.property_id]: !prev[row.property_id] }))}
                  className="mt-3 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                >
                  {expanded[row.property_id] ? "Hide Transactions" : "Show Transactions"}
                </button>
                {expanded[row.property_id] && (
                  <div className="mt-3 space-y-2">
                    {row.payments.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-white/15 py-4 text-center text-sm text-slate-300">
                        No payments recorded for this month.
                      </p>
                    ) : (
                      row.payments.map((payment) => (
                        <div key={payment.id} className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-white">{money(payment.amount)}</p>
                            <span className="text-xs text-slate-300">{payment.status}</span>
                          </div>
                          <p className="mt-1 text-xs text-slate-400">
                            {new Date(payment.created_at).toLocaleString()} - {payment.method}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

